if (!customElements.get("wls-main-product")) {
  class WLSMainProduct extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        slider: "wls-slider",
        thumbs: "[data-thumb]",
        variantSku: "[data-variant-sku]",
        variantPrice: "[data-variant-price]",
        variantData: "script[type='application/json'][id^='ProductVariants-']",
        variantPicker: "[data-wls-variant-picker]",
        addToCartForm: ".wls-main-product__atc-form",
        addToCartButton: "[data-wls-main-product-atc]",
        addToCartButtonLabel: "[data-wls-atc-label], .wls-main-product__atc-button-text",
        optionalAccessoriesCheckboxes: "input[name='optional_accessories[]']",
        breadPriceMessage: "[data-wls-bread-price]",
        breadPlacements: ".wls-main-product__price-financing, .wls-main-product__financing",
        quantityInput: ".wls-main-product__atc-form [data-quantity-value]",
      };

      this.onThumbClick = this.onThumbClick.bind(this);
      this.onSlideChange = this.onSlideChange.bind(this);
      this._onVideoFacadeClick = this._onVideoFacadeClick.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onVariantPillClick = this.onVariantPillClick.bind(this);
      this._onMobileZoomOpenClick = this._onMobileZoomOpenClick.bind(this);
      this._onMobileZoomDialogClick = this._onMobileZoomDialogClick.bind(this);
      this._onMobileZoomEscape = this._onMobileZoomEscape.bind(this);
      this._onMobileZoomSlideChange = this._onMobileZoomSlideChange.bind(this);
      this._onMobileZoomViewportChange = this._onMobileZoomViewportChange.bind(this);
      this._zoomTracksCleanup = [];
      this._zoomDebug = false;
      this._mobileZoomOpen = false;
      this.mobileZoomSwiper = null;
    }

    _zoomLog(step, detail) {
      if (!this._zoomDebug) return;
      console.log("[wls zoom]", step, detail !== undefined ? detail : "");
    }

    _destroyImageZoomTracks() {
      this._zoomLog("destroy: start", (this._zoomTracksCleanup || []).length);
      (this._zoomTracksCleanup || []).forEach((fn, i) => {
        this._zoomLog("destroy: track cleanup", i);
        try {
          fn();
        } catch (err) {
          this._zoomLog("destroy: cleanup error", String(err));
        }
      });
      this._zoomTracksCleanup = [];
      this._zoomLog("destroy: done");
    }

    _initImageZoomTracks() {
      const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this._zoomLog("init: media hover+fine", fine);
      this._zoomLog("init: prefers-reduced-motion (logged only; zoom still runs)", reduce);
      if (!fine) {
        this._zoomLog("init: aborted (need hover + fine pointer, e.g. mouse)");
        return;
      }
      const tracks = this.querySelectorAll("[data-wls-zoom-track]");
      this._zoomLog("init: [data-wls-zoom-track] count", tracks.length);
      tracks.forEach((root, idx) => {
        const overlay = root.querySelector("[data-wls-zoom-overlay]");
        const panEl = root.querySelector("[data-wls-zoom-pan]");
        const placeholderImg = root.querySelector(".wls-main-product__zoom-overlay-placeholder");
        const overlayImg = root.querySelector("[data-wls-zoom-overlay-img]");
        const zoomSrc = root.getAttribute("data-zoom-src") || "";
        this._zoomLog(`init: track ${idx} query overlay`, !!overlay);
        this._zoomLog(`init: track ${idx} query panEl`, !!panEl);
        this._zoomLog(`init: track ${idx} query placeholder`, !!placeholderImg);
        this._zoomLog(`init: track ${idx} query overlayImg`, !!overlayImg);
        this._zoomLog(`init: track ${idx} data-zoom-src length`, zoomSrc.length);

        const state = { srcAssigned: false, pendingPan: null };

        const sizeReady = () => {
          const hi =
            overlayImg &&
            overlayImg.src &&
            overlayImg.complete &&
            overlayImg.naturalWidth > 0;
          const lo =
            placeholderImg &&
            placeholderImg.complete &&
            placeholderImg.naturalWidth > 0 &&
            placeholderImg.offsetWidth >= 2;
          return hi || lo;
        };

        const pickSizeEl = () => {
          if (overlayImg && overlayImg.naturalWidth > 0) return overlayImg;
          if (
            placeholderImg &&
            placeholderImg.complete &&
            placeholderImg.naturalWidth > 0
          ) {
            return placeholderImg;
          }
          return null;
        };

        const updatePan = (clientX, clientY) => {
          this._zoomLog(`track ${idx}: updatePan called`, { clientX, clientY });
          if (!panEl) {
            this._zoomLog(`track ${idx}: updatePan skip (no panEl)`);
            return;
          }
          const sizeEl = pickSizeEl();
          if (!sizeEl) {
            this._zoomLog(`track ${idx}: updatePan skip (no size element yet)`);
            return;
          }
          const rect = root.getBoundingClientRect();
          if (rect.width < 2 || rect.height < 2) {
            this._zoomLog(`track ${idx}: updatePan skip (bad rect)`, rect);
            return;
          }
          let x = clientX - rect.left;
          let y = clientY - rect.top;
          x = Math.max(0, Math.min(x, rect.width));
          y = Math.max(0, Math.min(y, rect.height));
          const iw = sizeEl.offsetWidth;
          const ih = sizeEl.offsetHeight;
          const rw = rect.width;
          const rh = rect.height;
          this._zoomLog(`track ${idx}: updatePan sizes`, {
            iw,
            ih,
            rw,
            rh,
            usingHiRes: sizeEl === overlayImg,
          });
          if (iw < 2 || ih < 2) {
            this._zoomLog(`track ${idx}: updatePan skip (layout too small)`);
            return;
          }
          const maxTx = Math.min(0, rw - iw);
          const maxTy = Math.min(0, rh - ih);
          const tx = maxTx * (x / rw);
          const ty = maxTy * (y / rh);
          panEl.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
          this._zoomLog(`track ${idx}: updatePan applied transform on pan`, { tx, ty });
        };

        const onImgLoad = () => {
          this._zoomLog(`track ${idx}: overlay hi-res load event`, {
            naturalWidth: overlayImg.naturalWidth,
            naturalHeight: overlayImg.naturalHeight,
            currentSrc: overlayImg.currentSrc,
          });
          overlayImg.removeEventListener("load", onImgLoad);
          overlayImg.removeEventListener("error", onImgErr);
          if (overlay) overlay.classList.add("is-wls-zoom-hires-ready");
          this._zoomLog(`track ${idx}: hi-res ready class added (blur placeholder fades)`);
          if (state.pendingPan) {
            const { x, y } = state.pendingPan;
            state.pendingPan = null;
            this._zoomLog(`track ${idx}: apply pending pan after hi-res load`);
            updatePan(x, y);
          } else {
            const r = root.getBoundingClientRect();
            updatePan(r.left + r.width / 2, r.top + r.height / 2);
          }
        };

        const onImgErr = () => {
          this._zoomLog(`track ${idx}: overlay hi-res error`, overlayImg.src);
          overlayImg.removeEventListener("load", onImgLoad);
          overlayImg.removeEventListener("error", onImgErr);
        };

        const ensureSrc = () => {
          this._zoomLog(`track ${idx}: ensureSrc enter`);
          if (state.srcAssigned) {
            this._zoomLog(`track ${idx}: ensureSrc skip (already assigned)`);
            return;
          }
          if (!zoomSrc) {
            this._zoomLog(`track ${idx}: ensureSrc skip (empty data-zoom-src)`);
            return;
          }
          if (!overlayImg) {
            this._zoomLog(`track ${idx}: ensureSrc skip (no overlay img)`);
            return;
          }
          state.srcAssigned = true;
          overlayImg.addEventListener("load", onImgLoad);
          overlayImg.addEventListener("error", onImgErr);
          this._zoomLog(`track ${idx}: ensureSrc assigning hi-res src`, zoomSrc);
          overlayImg.src = zoomSrc;
          this._zoomLog(`track ${idx}: ensureSrc after assign`, {
            complete: overlayImg.complete,
            src: overlayImg.getAttribute("src"),
          });
        };

        const onEnter = (e) => {
          this._zoomLog(`track ${idx}: mouseenter`, { x: e.clientX, y: e.clientY });
          if (overlay) overlay.classList.remove("is-wls-zoom-hires-ready");
          ensureSrc();
          if (
            overlay &&
            overlayImg &&
            overlayImg.src &&
            overlayImg.complete &&
            overlayImg.naturalWidth
          ) {
            overlay.classList.add("is-wls-zoom-hires-ready");
            this._zoomLog(`track ${idx}: mouseenter hi-res already in cache`);
          }
          root.classList.add("is-wls-zoom-active");
          if (overlay) overlay.setAttribute("aria-hidden", "false");
          this._zoomLog(`track ${idx}: mouseenter active class + aria`);
          state.pendingPan = { x: e.clientX, y: e.clientY };
          updatePan(e.clientX, e.clientY);
          if (!sizeReady()) {
            this._zoomLog(`track ${idx}: mouseenter pan may wait for layout/hi-res`);
          }
        };

        const onMove = (e) => {
          this._zoomLog(`track ${idx}: mousemove`, { x: e.clientX, y: e.clientY });
          state.pendingPan = { x: e.clientX, y: e.clientY };
          if (!sizeReady()) {
            this._zoomLog(`track ${idx}: mousemove defer (no size yet)`);
            return;
          }
          updatePan(e.clientX, e.clientY);
        };

        const onLeave = () => {
          this._zoomLog(`track ${idx}: mouseleave`);
          root.classList.remove("is-wls-zoom-active");
          if (overlay) {
            overlay.setAttribute("aria-hidden", "true");
            overlay.classList.remove("is-wls-zoom-hires-ready");
          }
          if (panEl) panEl.style.transform = "";
          state.pendingPan = null;
          this._zoomLog(`track ${idx}: mouseleave reset done`);
        };

        root.addEventListener("mouseenter", onEnter);
        root.addEventListener("mousemove", onMove);
        root.addEventListener("mouseleave", onLeave);
        this._zoomLog(`track ${idx}: listeners attached`);

        this._zoomTracksCleanup.push(() => {
          this._zoomLog(`track ${idx}: removing listeners + img src`);
          root.removeEventListener("mouseenter", onEnter);
          root.removeEventListener("mousemove", onMove);
          root.removeEventListener("mouseleave", onLeave);
          if (overlayImg) {
            overlayImg.removeEventListener("load", onImgLoad);
            overlayImg.removeEventListener("error", onImgErr);
            overlayImg.removeAttribute("src");
          }
          if (overlay) overlay.classList.remove("is-wls-zoom-hires-ready");
          if (panEl) panEl.style.transform = "";
          onLeave();
        });
      });
      this._zoomLog("init: complete");
    }

    connectedCallback() {
      this.sliderEl = this.querySelector(this.selectors.slider);
      this.thumbs = Array.from(this.querySelectorAll(this.selectors.thumbs));
      this.variantSkuEl = this.querySelector(this.selectors.variantSku);
      this.variantPriceEl = this.querySelector(this.selectors.variantPrice);
      this.variantPickerEl = this.querySelector(this.selectors.variantPicker);

      const variantDataScript = this.querySelector(this.selectors.variantData);
      if (variantDataScript) {
        try {
          this.variantData = JSON.parse(variantDataScript.textContent);
        } catch (e) {
          console.error("Failed to parse variant data:", e);
        }
      }

      this.optionCount =
        Number(this.variantPickerEl?.dataset.optionCount) ||
        this.variantData?.option_count ||
        3;

      if (this.variantData?.variants?.length) {
        this.activeVariant = this.resolveInitialVariant();
        if (this.variantPickerEl) {
          this.refreshVariantPicker();
          this.variantPickerEl.addEventListener("click", this.onVariantPillClick);
        }
        this.updateAddToCartAvailability(this.activeVariant);
      }

      if (this.sliderEl && this.thumbs.length > 0) {
        this.tryAttachSwiper();
      }

      this._bindMobileZoomLightbox();

      this._zoomLog("connectedCallback: before _initImageZoomTracks");
      this._initImageZoomTracks();

      const addToCartForm = this.querySelector(this.selectors.addToCartForm);
      if (addToCartForm) {
        addToCartForm.addEventListener("submit", this.onFormSubmit);
      }

      // Click-to-load external video facades (delegated, covers gallery + mobile zoom)
      this.addEventListener("click", this._onVideoFacadeClick);

      // Sticky bar
      this._initStickyBar();

      this._initBreadPriceMessage();
      this._initBreadAmountEmphasis();
    }

    disconnectedCallback() {
      this._unbindMobileZoomLightbox();
      this._destroyMobileZoomSwiper();
      this._destroyImageZoomTracks();
      this.detachSwiper();
      this.thumbs?.forEach((thumb) =>
        thumb.removeEventListener("click", this.onThumbClick)
      );

      if (this.variantPickerEl && this.onVariantPillClick) {
        this.variantPickerEl.removeEventListener("click", this.onVariantPillClick);
      }

      const addToCartForm = this.querySelector(this.selectors.addToCartForm);
      if (addToCartForm && this.onFormSubmit) {
        addToCartForm.removeEventListener("submit", this.onFormSubmit);
      }

      this.removeEventListener("click", this._onVideoFacadeClick);

      if (this.quantityWrapperEl && this._onBreadQuantityChange) {
        ["click", "input", "change"].forEach((type) =>
          this.quantityWrapperEl.removeEventListener(type, this._onBreadQuantityChange)
        );
      }

      this._breadAmountObserver?.disconnect();

      // Sticky bar cleanup
      if (this._stickyObservers) {
        this._stickyObservers.forEach((obs) => obs.disconnect());
        this._stickyObservers = null;
      }
      this._stickyResizeObserver?.disconnect();
      document.body.classList.remove("wls-sticky-bar-visible");
    }

    _onVideoFacadeClick(event) {
      const facade = event.target.closest(".js-wls-video-facade");
      if (!facade || !this.contains(facade)) return;

      const host = (facade.dataset.videoHost || "").toLowerCase();
      const id = facade.dataset.videoId;
      if (!id) return;

      let src;
      if (host === "youtube") {
        src = `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
      } else if (host === "vimeo") {
        src = `https://player.vimeo.com/video/${id}?autoplay=1`;
      } else {
        return;
      }

      const iframe = document.createElement("iframe");
      iframe.className = facade.dataset.videoClass || "wls-main-product__content-media-main-video";
      iframe.setAttribute("frameborder", "0");
      iframe.setAttribute(
        "allow",
        "accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
      );
      iframe.setAttribute("allowfullscreen", "");
      iframe.setAttribute("title", facade.getAttribute("aria-label") || "Video");
      iframe.src = src;

      facade.replaceWith(iframe);
    }

    resolveInitialVariant() {
      const variants = this.variantData.variants;
      const urlId = new URL(window.location.href).searchParams.get("variant");
      const fromUrl = urlId ? variants.find((v) => Number(v.id) === Number(urlId)) : null;
      const formInput = this.querySelector('form[action*="/cart/add"] input[name="id"]');
      const fromForm = formInput?.value
        ? variants.find((v) => Number(v.id) === Number(formInput.value))
        : null;
      return fromUrl || fromForm || variants[0];
    }

    normalizeOption(value) {
      if (value == null || value === "") return "";
      return String(value);
    }

    selectionFromVariant(variant) {
      return [
        this.normalizeOption(variant.option1),
        this.normalizeOption(variant.option2),
        this.normalizeOption(variant.option3),
      ];
    }

    getOptionValue(variant, index) {
      return this.selectionFromVariant(variant)[index] ?? "";
    }

    variantMatchesSelection(variant, selection) {
      const n = this.optionCount;
      for (let i = 0; i < n; i++) {
        if (this.getOptionValue(variant, i) !== (selection[i] ?? "")) {
          return false;
        }
      }
      return true;
    }

    findVariantForSelection(selection) {
      return this.variantData.variants.find((v) =>
        this.variantMatchesSelection(v, selection)
      );
    }

    findVariantsForOption(optionIndex, value) {
      const want = this.normalizeOption(value);
      return this.variantData.variants.filter(
        (v) => this.getOptionValue(v, optionIndex) === want
      );
    }

    /** Prefer in-stock, otherwise first matching variant for this option value. */
    findVariantFallback(optionIndex, value) {
      const matches = this.findVariantsForOption(optionIndex, value);
      return matches.find((v) => v.available) || matches[0];
    }

    onVariantPillClick(e) {
      const btn = e.target.closest("[data-wls-variant-pill]");
      if (!btn || !this.variantPickerEl.contains(btn)) return;

      if (btn.disabled || btn.classList.contains("is-invalid")) return;

      const optionIndex = Number(btn.dataset.optionIndex);
      const value = btn.getAttribute("data-option-value");
      if (!Number.isFinite(optionIndex) || value == null) return;

      const base = this.selectionFromVariant(this.activeVariant);
      const nextSel = base.slice();
      nextSel[optionIndex] = value;

      const exact = this.findVariantForSelection(nextSel);
      let variant;
      if (optionIndex === 0) {
        // Keep the current secondary options only when that combo is in stock;
        // otherwise select the first available combination for this first option.
        variant =
          (exact?.available ? exact : null) ||
          this.findVariantFallback(optionIndex, value) ||
          exact;
      } else {
        variant = exact || this.findVariantFallback(optionIndex, value);
      }
      if (!variant) return;

      this.applyVariant(variant);
    }

    refreshVariantPicker() {
      if (!this.variantPickerEl || !this.activeVariant) return;

      const base = this.selectionFromVariant(this.activeVariant);

      this.variantPickerEl.querySelectorAll("[data-variant-group-selection]").forEach((el) => {
        const i = Number(el.dataset.optionGroupIndex);
        if (Number.isFinite(i) && i < this.optionCount) {
          el.textContent = base[i] || "";
        }
      });

      this.variantPickerEl.querySelectorAll("[data-wls-variant-pill]").forEach((btn) => {
        const idx = Number(btn.dataset.optionIndex);
        const val = btn.getAttribute("data-option-value");
        const isCurrent = this.normalizeOption(val) === this.normalizeOption(base[idx]);

        btn.classList.toggle("is-active", isCurrent);
        btn.setAttribute("aria-pressed", isCurrent ? "true" : "false");

        let invalid;
        let oos;

        if (idx === 0) {
          // First option: every value that has a variant stays selectable, in stock
          // or not — picking it re-resolves the other options.
          const matches = this.findVariantsForOption(0, val);
          const hasAvailable = matches.some((v) => v.available);
          invalid = matches.length === 0;
          oos = matches.length > 0 && !hasAvailable;
        } else {
          // Other options: the value is only offered when it exists together with
          // the current selection. Out of stock combinations stay selectable so the
          // buy button can show its out-of-stock state.
          const testSel = base.slice();
          testSel[idx] = val;
          const match = this.findVariantForSelection(testSel);
          invalid = !match;
          oos = Boolean(match && !match.available);
        }

        // A value that is not a real combination is hidden, not disabled.
        btn.hidden = invalid;
        btn.classList.toggle("is-invalid", invalid);
        btn.classList.toggle("is-unavailable", !invalid && oos);
        btn.disabled = false;
      });
    }

    _isMobileZoomViewport() {
      return window.matchMedia("(max-width: 767px)").matches;
    }

    _bindMobileZoomLightbox() {
      this.mobileZoomOpenBtn = this.querySelector("[data-wls-mobile-zoom-open]");
      this.mobileZoomDialog = this.querySelector("[data-wls-mobile-zoom-dialog]");
      if (!this.mobileZoomOpenBtn || !this.mobileZoomDialog) return;

      this.mobileZoomOpenBtn.addEventListener("click", this._onMobileZoomOpenClick);
      this.mobileZoomDialog.addEventListener("click", this._onMobileZoomDialogClick);

      this._mobileZoomMm = window.matchMedia("(max-width: 767px)");
      this._mobileZoomMm.addEventListener("change", this._onMobileZoomViewportChange);
    }

    _unbindMobileZoomLightbox() {
      this._closeMobileZoomDialog();
      if (this._mobileZoomMm && this._onMobileZoomViewportChange) {
        this._mobileZoomMm.removeEventListener("change", this._onMobileZoomViewportChange);
      }
      if (this.mobileZoomOpenBtn && this._onMobileZoomOpenClick) {
        this.mobileZoomOpenBtn.removeEventListener("click", this._onMobileZoomOpenClick);
      }
      if (this.mobileZoomDialog && this._onMobileZoomDialogClick) {
        this.mobileZoomDialog.removeEventListener("click", this._onMobileZoomDialogClick);
      }
      this.mobileZoomOpenBtn = null;
      this.mobileZoomDialog = null;
    }

    _onMobileZoomViewportChange() {
      if (!this._mobileZoomMm.matches) this._closeMobileZoomDialog();
    }

    _onMobileZoomOpenClick(e) {
      e.preventDefault();
      e.stopPropagation();
      if (!this._isMobileZoomViewport()) return;
      this._openMobileZoomDialog();
    }

    _onMobileZoomDialogClick(e) {
      if (!this._mobileZoomOpen) return;
      if (e.target.closest("[data-wls-mobile-zoom-close]")) {
        e.preventDefault();
        this._closeMobileZoomDialog();
      }
    }

    _onMobileZoomEscape(e) {
      if (e.key === "Escape") this._closeMobileZoomDialog();
    }

    _onMobileZoomSlideChange() {
      if (!this.mobileZoomSwiper || this.mobileZoomSwiper.destroyed) return;
      const idx = this.mobileZoomSwiper.activeIndex;
      if (this.swiper && !this.swiper.destroyed && this.swiper.activeIndex !== idx) {
        this.swiper.slideTo(idx);
        this.setActiveThumb(idx);
      }
    }

    _openMobileZoomDialog() {
      if (this._mobileZoomOpen || !this.mobileZoomDialog) return;
      const slideCount = Number(this.mobileZoomDialog.dataset.slideCount || 0);
      if (slideCount < 1) return;

      this._mobileZoomPreviouslyFocused = document.activeElement;

      this._mobileZoomOpen = true;
      this.mobileZoomDialog.removeAttribute("hidden");
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", this._onMobileZoomEscape);

      const initial = this.getActiveIndex();
      this._initMobileZoomSwiperWhenReady(initial, 0);

      requestAnimationFrame(() => {
        const closeBtn = this.mobileZoomDialog.querySelector("[data-wls-mobile-zoom-close].wls-main-product__mobile-zoom-close");
        closeBtn?.focus({ preventScroll: true });
      });
    }

    _closeMobileZoomDialog() {
      if (!this._mobileZoomOpen || !this.mobileZoomDialog) return;

      this._destroyMobileZoomSwiper();
      this.mobileZoomDialog.setAttribute("hidden", "");
      document.body.style.overflow = "";
      document.removeEventListener("keydown", this._onMobileZoomEscape);
      this._mobileZoomOpen = false;

      const refocus = this._mobileZoomPreviouslyFocused;
      this._mobileZoomPreviouslyFocused = null;
      if (refocus && typeof refocus.focus === "function") {
        try {
          refocus.focus({ preventScroll: true });
        } catch (_) {
          refocus.focus();
        }
      }
    }

    _destroyMobileZoomSwiper() {
      if (this.mobileZoomSwiper && !this.mobileZoomSwiper.destroyed) {
        this.mobileZoomSwiper.destroy(true, true);
      }
      this.mobileZoomSwiper = null;
    }

    _initMobileZoomSwiperWhenReady(initialIndex, attempts) {
      if (!this._mobileZoomOpen || !this.mobileZoomDialog) return;
      if (typeof Swiper === "undefined") {
        if (attempts < 40) {
          requestAnimationFrame(() => this._initMobileZoomSwiperWhenReady(initialIndex, attempts + 1));
        }
        return;
      }

      const el = this.mobileZoomDialog.querySelector(".js-wls-mobile-zoom-swiper");
      if (!el) return;

      this._destroyMobileZoomSwiper();

      const slideCount = Number(this.mobileZoomDialog.dataset.slideCount || 0);
      const maxIdx = Math.max(0, slideCount - 1);
      const start = Math.min(Math.max(0, initialIndex), maxIdx);
      const pagEl = this.mobileZoomDialog.querySelector("[data-wls-mobile-zoom-pagination]");

      const swiperOptions = {
        initialSlide: start,
        speed: 300,
        slidesPerView: 1,
        spaceBetween: 0,
        zoom: {
          maxRatio: 4,
          minRatio: 1,
          toggle: true,
        },
        on: {
          slideChange: this._onMobileZoomSlideChange,
        },
      };

      if (pagEl && slideCount > 1) {
        swiperOptions.pagination = {
          el: pagEl,
          clickable: true,
        };
      }

      this.mobileZoomSwiper = new Swiper(el, swiperOptions);
    }

    tryAttachSwiper() {
      const swiper = this.sliderEl?.swiperInstance;

      if (!swiper || swiper.destroyed) {
        requestAnimationFrame(() => this.tryAttachSwiper());
        return;
      }

      this.swiper = swiper;

      this.thumbs.forEach((thumb) =>
        thumb.addEventListener("click", this.onThumbClick)
      );

      this.swiper.on("slideChange", this.onSlideChange);

      this.setActiveThumb(this.getActiveIndex());
    }

    detachSwiper() {
      if (this.swiper && !this.swiper.destroyed) {
        this.swiper.off("slideChange", this.onSlideChange);
      }
      this.swiper = null;
    }

    onThumbClick(e) {
      const btn = e.currentTarget;
      const index = Number(btn.dataset.index);

      if (!Number.isFinite(index) || !this.swiper) return;

      this.swiper.slideTo(index);
      this.setActiveThumb(index);
    }

    onSlideChange() {
      this.setActiveThumb(this.getActiveIndex());
    }

    getActiveIndex() {
      return this.swiper?.activeIndex ?? 0;
    }

    setActiveThumb(index) {
      this.thumbs.forEach((thumb, i) => {
        const isActive = i === index;
        thumb.classList.toggle("is-active", isActive);
        if (isActive) thumb.setAttribute("aria-current", "true");
        else thumb.removeAttribute("aria-current");
      });
    }

    applyVariant(variant) {
      this.activeVariant = variant;
      if (this.variantSkuEl) this.variantSkuEl.textContent = `SKU: ${variant.sku || ""}`;
      if (this.variantPriceEl) this.updatePrice(variant);

      this.updateMediaForVariant(variant);

      this.updateUrlVariant(variant.id);
      this.updateProductFormVariantId(variant.id);

      if (this.variantPickerEl) this.refreshVariantPicker();

      this.updateAddToCartAvailability(variant);
      this._updateStickyBar(variant);
      this.updateBreadPriceMessage();
      this.notifyVariantChange(variant);
    }

    _initBreadPriceMessage() {
      this.breadPriceEl = this.querySelector(this.selectors.breadPriceMessage);
      if (!this.breadPriceEl) return;

      this.quantityInputEl = this.querySelector(this.selectors.quantityInput);
      this.quantityWrapperEl = this.quantityInputEl?.closest("[data-quantity-wrapper]");
      if (this.quantityWrapperEl) {
        // The theme's +/- handler runs after this click and only fires a jQuery change
        this._onBreadQuantityChange = () => setTimeout(() => this.updateBreadPriceMessage(), 0);
        ["click", "input", "change"].forEach((type) =>
          this.quantityWrapperEl.addEventListener(type, this._onBreadQuantityChange)
        );
      }

      this.updateBreadPriceMessage();
    }

    /**
     * The Bread app writes "As low as $85.33/mo" as plain text and rewrites it on every
     * price or quantity change, so the monthly amount is wrapped again after each render.
     */
    _initBreadAmountEmphasis() {
      const placements = this.querySelectorAll(this.selectors.breadPlacements);
      if (!placements.length) return;

      const emphasize = () => {
        this.querySelectorAll(".bread-inner-a").forEach((link) => {
          if (link.querySelector(".wls-main-product__bread-amount")) return;
          const match = link.textContent.match(/^(.*?)(\$[\d,.]+\/mo)(.*)$/);
          if (!match) return;
          const amount = document.createElement("span");
          amount.className = "wls-main-product__bread-amount";
          amount.textContent = match[2];
          link.replaceChildren(match[1], amount, match[3]);
        });
      };

      this._breadAmountObserver = new MutationObserver(emphasize);
      placements.forEach((el) => this._breadAmountObserver.observe(el, { childList: true, subtree: true }));
      emphasize();
    }

    /**
     * The Bread app prices its placements as variant price x quantity and leaves them
     * empty outside its financing bounds, so the price row message is hidden there.
     */
    updateBreadPriceMessage() {
      if (!this.breadPriceEl || !this.activeVariant) return;
      const quantity = parseInt(this.quantityInputEl?.value, 10) || 1;
      const amount = Number(this.activeVariant.price) * quantity;
      const { minAmount, maxAmount } = this.breadPriceEl.dataset;
      this.breadPriceEl.hidden = amount < Number(minAmount) || amount > Number(maxAmount);
    }

    /**
     * Third-party scripts (Klaviyo back-in-stock "Notify Me When Available",
     * financing widgets) watch for a bubbling `change` on the product form, which
     * a button based picker never fires on its own.
     */
    notifyVariantChange(variant) {
      const input = this.querySelector(
        'form[action*="/cart/add"] input[name="id"]'
      );
      if (input) input.dispatchEvent(new Event("change", { bubbles: true }));

      document.dispatchEvent(
        new CustomEvent("variant:change", {
          bubbles: true,
          detail: { variant },
        })
      );
    }

    updateAddToCartAvailability(variant) {
      const form = this.querySelector(this.selectors.addToCartForm);
      const btn =
        form?.querySelector(this.selectors.addToCartButton) ||
        this.querySelector(this.selectors.addToCartButton);
      if (!btn) return;

      const id = variant?.id;
      const fromStore =
        id != null && Array.isArray(this.variantData?.variants)
          ? this.variantData.variants.find(
              (x) => Number(x.id) === Number(id)
            )
          : null;
      const v = fromStore || variant;

      const productUnavailable =
        this.getAttribute("data-product-available") === "false";
      const a = v?.available;
      const variantPurchasable =
        v != null && (a === true || a === "true");

      const canAddToCart = !productUnavailable && variantPurchasable;

      btn.disabled = !canAddToCart;
      btn.classList.toggle("is-unavailable", !canAddToCart);
      if (canAddToCart) btn.removeAttribute("aria-disabled");
      else btn.setAttribute("aria-disabled", "true");

      this.setAddToCartLabel(
        btn,
        canAddToCart ? btn.dataset.atcText : btn.dataset.atcOosText
      );
    }

    /** Writes the button label without destroying the inner label markup. */
    setAddToCartLabel(btn, text) {
      if (!btn || text == null || text === "") return;
      const labelEl = btn.querySelector(this.selectors.addToCartButtonLabel);
      if (labelEl) labelEl.textContent = text;
      else btn.textContent = text;
    }

    getAddToCartLabel(btn) {
      if (!btn) return "";
      const labelEl = btn.querySelector(this.selectors.addToCartButtonLabel);
      return (labelEl || btn).textContent;
    }

    updatePrice(variant) {
      const price = variant.price;
      const compareAtPrice = variant.compare_at_price;

      let formattedPrice = "";
      if (window.Shopify && window.Shopify.formatMoney) {
        formattedPrice = window.Shopify.formatMoney(price);
      } else if (window.T4SThemeSP && window.T4SThemeSP.Currency && window.T4SThemeSP.Currency.formatMoney) {
        formattedPrice = window.T4SThemeSP.Currency.formatMoney(price);
      } else {
        formattedPrice = `$${(price / 100).toFixed(2)}`;
      }

      let priceHTML = formattedPrice;

      if (compareAtPrice && compareAtPrice > price) {
        let formattedComparePrice = "";
        if (window.Shopify && window.Shopify.formatMoney) {
          formattedComparePrice = window.Shopify.formatMoney(compareAtPrice);
        } else if (window.T4SThemeSP && window.T4SThemeSP.Currency && window.T4SThemeSP.Currency.formatMoney) {
          formattedComparePrice = window.T4SThemeSP.Currency.formatMoney(compareAtPrice);
        } else {
          formattedComparePrice = `$${(compareAtPrice / 100).toFixed(2)}`;
        }
        const isCPO = this.hasAttribute("data-product-cpo");
        if (isCPO) {
          priceHTML = `<span class="t4s-price-cpo-label" data-wls-cpo-label>New:</span> <span class="t4s-price-cpo-compare">${formattedComparePrice}</span> <span class="wls-main-product__price-sale">${formattedPrice}</span>`;
        } else {
          priceHTML = `<span class="wls-main-product__price-compare">${formattedComparePrice}</span> <span class="wls-main-product__price-sale">${formattedPrice}</span>`;
        }
      }

      const priceContentEl = this.variantPriceEl.querySelector("[data-wls-price-content]");
      if (priceContentEl) {
        priceContentEl.innerHTML = priceHTML;
      } else {
        const priceContainer = this.variantPriceEl.querySelector(".t4s-product-price");
        if (priceContainer) {
          priceContainer.innerHTML = priceHTML;
        } else {
          this.variantPriceEl.innerHTML = priceHTML;
        }
      }

      const saveBadge = this.variantPriceEl.querySelector("[data-wls-save-badge]");
      if (saveBadge) {
        if (compareAtPrice && compareAtPrice > price) {
          const isCPO = this.hasAttribute("data-product-cpo");
          const saveAmount = compareAtPrice - price;
          let formattedSaveAmount = "";
          if (window.Shopify && window.Shopify.formatMoney) {
            formattedSaveAmount = window.Shopify.formatMoney(saveAmount);
          } else if (window.T4SThemeSP && window.T4SThemeSP.Currency && window.T4SThemeSP.Currency.formatMoney) {
            formattedSaveAmount = window.T4SThemeSP.Currency.formatMoney(saveAmount);
          } else {
            formattedSaveAmount = `$${(saveAmount / 100).toFixed(2)}`;
          }
          const amountEl = saveBadge.querySelector("[data-wls-save-amount]");
          if (amountEl) amountEl.textContent = formattedSaveAmount;
          const suffixEl = saveBadge.querySelector("[data-wls-save-suffix]");
          if (suffixEl) suffixEl.textContent = isCPO ? " vs. new" : "";
          saveBadge.hidden = false;
        } else {
          saveBadge.hidden = true;
        }
      }
    }

    updateMediaForVariant(variant) {
      if (!this.swiper || this.swiper.destroyed) return;
    
      const ids = Array.isArray(variant.media_ids) ? variant.media_ids : [];
      const targetMediaId = ids.length ? Number(ids[0]) : Number(variant.featured_media);
    
      if (!Number.isFinite(targetMediaId)) return;
    
      const slides = Array.from(this.querySelectorAll(".swiper-slide"));
      const index = slides.findIndex((s) => Number(s.dataset.mediaId) === targetMediaId);
    
      if (index < 0) return;
    
      this.swiper.slideTo(index);
      this.setActiveThumb(index);
    }
    
    updateUrlVariant(variantId) {
      const id = Number(variantId);
      if (!Number.isFinite(id)) return;
    
      const url = new URL(window.location.href);
      url.searchParams.set("variant", String(id));
      window.history.replaceState({}, "", url.toString());
    }
    
    
    updateProductFormVariantId(variantId) {
      const input = this.querySelector('form[action*="/cart/add"] input[name="id"]');
      if (input) input.value = variantId;
    }

    onFormSubmit(e) {
      const form = e.currentTarget;
      const submitButton = form.querySelector(this.selectors.addToCartButton);
      if (submitButton?.disabled) {
        e.preventDefault();
        return;
      }

      e.preventDefault();

      const variantIdInput = form.querySelector('input[name="id"]');
      const quantityInput = form.querySelector('input[name="quantity"]');

      if (!variantIdInput) {
        console.error("Variant ID input not found");
        return;
      }

      const variantId = variantIdInput.value;
      const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

      const checkedAccessories = Array.from(
        this.querySelectorAll(`${this.selectors.optionalAccessoriesCheckboxes}:checked`)
      ).map((checkbox) => ({
        id: checkbox.value,
        quantity: 1,
      }));

      const items = [
        {
          id: variantId,
          quantity: quantity,
        },
        ...checkedAccessories,
      ];

      if (submitButton) {
        submitButton.disabled = true;
        const originalText = this.getAddToCartLabel(submitButton);
        this.setAddToCartLabel(submitButton, "Adding...");

        // Sync loading state to sticky bar ATC
        const stickyAtc = this._stickyBar?.querySelector("[data-wls-sticky-atc]");
        if (stickyAtc) {
          stickyAtc.disabled = true;
          stickyAtc.setAttribute("data-original-text", stickyAtc.textContent);
          stickyAtc.textContent = "Adding...";
        }

        this.addItemsToCart(items)
          .then(() => {
            submitButton.disabled = false;
            this.setAddToCartLabel(submitButton, originalText);
            if (stickyAtc) {
              stickyAtc.disabled = false;
              stickyAtc.textContent = stickyAtc.getAttribute("data-original-text") || "ADD TO CART";
            }

            document.dispatchEvent(
              new CustomEvent("cart:add", {
                detail: { items },
                bubbles: true,
              })
            );
            document.dispatchEvent(
              new CustomEvent("cart:updated", {
                bubbles: true,
              })
            );
            document.dispatchEvent(
              new CustomEvent("cart:refresh", {
                bubbles: true,
              })
            );

            // The slide-out cart opens itself off cart:add. Only fall back to
            // the cart page when the drawer isn't on this template.
            if (!document.querySelector("[data-wls-cart-drawer]")) {
              window.location.href = "/cart";
            }
          })
          .catch((error) => {
            console.error("Error adding to cart:", error);
            submitButton.disabled = false;
            this.setAddToCartLabel(submitButton, originalText);
            if (stickyAtc) {
              stickyAtc.disabled = false;
              stickyAtc.textContent = stickyAtc.getAttribute("data-original-text") || "ADD TO CART";
            }
            alert("Failed to add product to cart. Please try again.");
          });
      }
    }

    addItemsToCart(items) {
      return fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: items,
        }),
      }).then((response) => {
        if (!response.ok) {
          return response.json().then((error) => {
            throw new Error(error.description || "Failed to add to cart");
          });
        }
        return response.json();
      });
    }
    // ─── Sticky Bar ───
    _initStickyBar() {
      this._stickyBarWrapper = this.querySelector("[data-wls-sticky-bar]");
      this._stickyBar = this._stickyBarWrapper?.querySelector(".wls-main-product__sticky-bar");
      if (!this._stickyBarWrapper) return;

      const atcButton = this.querySelector(this.selectors.addToCartButton);
      if (!atcButton) return;

      // Show sticky bar when ATC is out of view and footer is not visible.
      // Hide conditions: elements whose visibility hides the sticky bar.
      this._stickyBarWrapper.hidden = false;
      this._stickyShow = false;   // true when ATC scrolls out of view (above viewport)
      this._stickyHideFooter = false; // true when footer enters viewport

      this._stickyObservers = [];

      // Keep --wls-sticky-bar-height in sync (the variant name can wrap), so the back to top
      // button and the Norton seal can sit above the bar
      if ("ResizeObserver" in window) {
        this._stickyResizeObserver = new ResizeObserver(() => this._updateStickyBarHeight());
        this._stickyResizeObserver.observe(this._stickyBarWrapper);
      }
      this._updateStickyBarHeight();

      // Show when ATC button leaves the viewport from the top
      const atcObserver = new IntersectionObserver(
        ([entry]) => {
          this._stickyShow = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          this._updateStickyVisibility();
        },
        { threshold: 0 }
      );
      atcObserver.observe(atcButton);
      this._stickyObservers.push(atcObserver);

      // Hide when footer is visible
      const footer = document.querySelector("#wls-footer, .wls-footer");
      if (footer) {
        const footerObserver = new IntersectionObserver(
          ([entry]) => {
            this._stickyHideFooter = entry.intersectionRatio > 0.1;
            this._updateStickyVisibility();
          },
          { threshold: [0, 0.1, 0.2] }
        );
        footerObserver.observe(footer);
        this._stickyObservers.push(footerObserver);
      }

      // CHANGE OPTIONS button scrolls to the variant picker
      const modifyBtn = this._stickyBar.querySelector("[data-wls-sticky-modify]");
      if (modifyBtn) {
        modifyBtn.addEventListener("click", () => {
          const target = this.variantPickerEl || this.querySelector(this.selectors.addToCartForm);
          if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        });
      }

      // ATC button is type="submit" inside the form — no extra JS needed
    }

    _updateStickyVisibility() {
      if (!this._stickyBarWrapper) return;
      const show = this._stickyShow && !this._stickyHideFooter;
      this._stickyBarWrapper.classList.toggle("is-visible", show);
      // Lets fixed elements outside the section (back to top, Norton seal) move above the bar
      document.body.classList.toggle("wls-sticky-bar-visible", show);
    }

    _updateStickyBarHeight() {
      if (!this._stickyBarWrapper) return;
      document.documentElement.style.setProperty(
        "--wls-sticky-bar-height",
        `${this._stickyBarWrapper.offsetHeight}px`
      );
    }

    _updateStickyBar(variant) {
      if (!this._stickyBar) return;

      // Update variant name
      const variantEl = this._stickyBar.querySelector("[data-wls-sticky-variant]");
      if (variantEl) {
        variantEl.textContent = [variant.option1, variant.option2, variant.option3].filter(Boolean).join(" / ");
      }

      // Update price
      const priceEls = this._stickyBar.querySelectorAll("[data-wls-sticky-price]");

      if (priceEls.length) {
        const price = variant.price;
        const compareAtPrice = variant.compare_at_price;
        let formattedPrice = "";
        let formattedCompare = "";

        if (window.Shopify && window.Shopify.formatMoney) {
          formattedPrice = window.Shopify.formatMoney(price);
          if (compareAtPrice) formattedCompare = window.Shopify.formatMoney(compareAtPrice);
        } else if (
          window.T4SThemeSP &&
          window.T4SThemeSP.Currency &&
          window.T4SThemeSP.Currency.formatMoney
        ) {
          formattedPrice = window.T4SThemeSP.Currency.formatMoney(price);
          if (compareAtPrice) formattedCompare = window.T4SThemeSP.Currency.formatMoney(compareAtPrice);
        } else {
          formattedPrice = `$${(price / 100).toFixed(2)}`;
          if (compareAtPrice) formattedCompare = `$${(compareAtPrice / 100).toFixed(2)}`;
        }

        priceEls.forEach((priceEl) => {
          if (compareAtPrice && compareAtPrice > price) {
            priceEl.innerHTML = `
              <span class="wls-main-product__sticky-bar-compare">${formattedCompare}</span>
              <span class="wls-main-product__sticky-bar-sale">${formattedPrice}</span>
            `;
          } else {
            priceEl.innerHTML = `
              <span class="wls-main-product__sticky-bar-sale">${formattedPrice}</span>
            `;
          }
        });
      }

      // Update ATC button state
      const stickyAtc = this._stickyBar.querySelector("[data-wls-sticky-atc]");
      if (stickyAtc) {
        stickyAtc.disabled = !variant.available;
        stickyAtc.textContent = variant.available ? "ADD TO CART" : "OUT OF STOCK";
      }
    }
  }

  customElements.define("wls-main-product", WLSMainProduct);
}
